import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import withLayout from 'hocs/withLayout';
import { FeaturedPostGrid, PostGrid } from 'organisms';
import { HomePosts } from 'types/home';
import useStyles from './styles';
import { AdSpace } from 'molecules';
import { reportError } from 'lib/errors';
import { ScrollToTop, QueryResult, SectionHeader } from '@elevator/ui';
import { LoadingError } from 'atoms';
import { EMPTY_POST_CARD } from './Home.utils';

/**
 * Props for the Home component
 */
interface HomePropTypes extends QueryResult<
  'homePosts',
  HomePosts,
  { skip: number; limit: number }
> {
  /**
   * Whether more posts are currently being loaded
   */
  loadingMore: boolean;
}

/**
 * Home Component
 *
 * Main presentation component for the home page. Displays featured posts grid,
 * paginated post teasers, and ad spaces.
 *
 * Features:
 * - Featured posts grid (positions 1-4)
 * - Paginated post teasers with load more functionality
 * - Ad space integration
 * - Scroll to top button
 *
 * @param props - Component props including home posts data and query functions
 * @returns Home page content with featured posts, teasers, and ads
 */
const Home = ({
  variables,
  fetchMore,
  homePosts,
  error,
  loadingMore,
  loading,
}: HomePropTypes): React.JSX.Element => {
  const { classes } = useStyles();

  // Display limit state - resets on mount (component remounts on navigation)
  const [displayLimit, setDisplayLimit] = useState(variables.limit);
  const [revealAfterLength, setRevealAfterLength] = useState<number | null>(null);

  // Slice posts array based on displayLimit
  const allPosts = homePosts?.publishedPosts || [];
  const displayedPosts = allPosts.slice(0, displayLimit);

  // After fetchMore, reveal the next page once cache length grows
  if (revealAfterLength != null && allPosts.length > revealAfterLength) {
    setRevealAfterLength(null);
    setDisplayLimit((prev) => prev + variables.limit);
  }

  if (error) {
    return (
      <LoadingError
        error={error}
        failedTarget={'home page posts'}
        reportError={reportError}
      />
    );
  }

  /**
   * Handles loading more posts when user clicks "Load More" button
   *
   * If more posts exist in cache, instantly increases display limit.
   * Otherwise, fetches more posts from the server.
   */
  const onLoadMore = (): void => {
    // Check if more posts exist in cache
    if (allPosts.length > displayLimit) {
      // More posts available in cache - just increase display limit (instant)
      setDisplayLimit((prev) => prev + variables.limit);
    } else {
      // Need to fetch more posts; reveal once cache grows
      setRevealAfterLength(allPosts.length);
      fetchMore({
        variables: {
          limit: variables.limit,
          skip: allPosts.length,
        },
        updateQuery: (
          prev: HomePosts,
          { fetchMoreResult }: { fetchMoreResult?: HomePosts },
        ) => {
          if (!fetchMoreResult) return prev;
          const newPosts = [...prev.publishedPosts, ...fetchMoreResult.publishedPosts];
          return {
            ...prev,
            publishedPosts: newPosts,
          };
        },
      });
    }
  };

  /**
   * Renders an ad space component
   *
   * @param unit - Ad unit identifier (e.g., 'ELEVATOR-Mid', 'ELEVATOR-Bottom')
   * @returns Ad space component wrapped in styled container
   */
  const renderAdSpace = (unit: string): React.JSX.Element => {
    return (
      <Stack
        component="aside"
        className={classes.adSpace}
        direction="row"
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <AdSpace type={'banner-ad'} adUnit={unit} size="responsive" />
      </Stack>
    );
  };

  return (
    <>
      <Box className={classes.container}>
        {homePosts?.homepageSettings?.showFeatured === false ? null : (
          <Box component="section" aria-label="Featured Posts">
            <FeaturedPostGrid
              position1Post={homePosts?.featuredPosts?._1 || EMPTY_POST_CARD}
              position2Post={homePosts?.featuredPosts?._2 || EMPTY_POST_CARD}
              position3Post={homePosts?.featuredPosts?._3 || EMPTY_POST_CARD}
              position4Post={homePosts?.featuredPosts?._4 || EMPTY_POST_CARD}
              isLoading={loading}
              error={error}
            />
          </Box>
        )}
        {renderAdSpace('ELEVATOR-Mid')}
        <Box
          component="section"
          aria-label="Latest Posts"
          className={classes.latestPostsSection}
        >
          <SectionHeader title="Latest" />
          <PostGrid
            posts={displayedPosts}
            onLoadMore={onLoadMore}
            error={error}
            loadingMore={loadingMore}
            isLoading={loading}
            adUnitPrefix="ELEVATOR-Mid"
          />
        </Box>
      </Box>
      <ScrollToTop visibleYOffset={800} />
    </>
  );
};

export default withLayout(Home, { bottomAd: true });
